import { ElementType } from '@fiverr-private/bianka_js';
import { BiankaEventType } from '@fiverr-private/obs';
import { STEP_SLUGS, Store } from '@fiverr-private/store_builder_types';
import { TabbedCarouselTabClickData } from '@fiverr-private/personalized_content/dist/esm/src/PersonalizedContent/components/TabsView/types';

export const stepMetaMap: Record<
    (typeof STEP_SLUGS)[number],
    {
        name: string;
        impressionName: string;
        fields?: (keyof Partial<Store>)[];
    }
> = {
    'choose-niche': {
        name: 'bys_niche_choice',
        impressionName: 'viewed_bys_wizard_niche_cards',
        fields: ['id', 'niche'],
    },
    'choose-banners': {
        name: 'bys_banner_choice',
        impressionName: 'viewed_bys_wizard_banner_choice',
        fields: ['id', 'banners'],
    },
    'choose-selling-plan': {
        name: 'bys_product_type',
        impressionName: 'viewed_bys_wizard_product_type',
        fields: ['id', 'dropshipper'],
    },
    'sign-up': {
        name: 'bys_shopify_sign_up',
        impressionName: 'viewed_bys_shopify_sign_up',
        fields: ['id'],
    },
    'link-your-store': {
        name: 'bys_access_shopify',
        impressionName: 'viewed_bys_access_shopify',
        fields: ['id', 'storeUrl'],
    },
    'install-app': {
        name: 'bys_install_app',
        impressionName: 'viewed_bys_install_app',
        fields: ['id', 'appInstalled'],
    },
    subscribe: {
        name: 'bys_subscribe_to_shopify',
        impressionName: 'viewed_bys_subscribe_to_shopify',
        fields: ['id', 'niche', 'banners', 'dropshipper', 'storeUrl', 'appInstalled'],
    },
    'create-store': {
        name: 'bys_creating_shopify_store',
        impressionName: 'viewed_bys_creating_shopify_store',
        fields: ['id'],
    },
    'additional-services': {
        name: 'bys_make_customer_ready_with_gigs',
        impressionName: 'viewed_bys_make_customer_ready',
        fields: ['id'],
    },
    'subscribe-autods': {
        name: 'bys_autods_sign_up',
        impressionName: 'viewed_bys_autods_sign_up',
        fields: ['id'],
    },
    publish: {
        name: 'bys_publish_store',
        impressionName: 'viewed_bys_publish_store',
        fields: ['id'],
    },
};

export const getStoreByElementName = (
    store: Partial<Store> | undefined,
    elementName: (typeof STEP_SLUGS)[number]
): Record<string, any> => {
    const fields = stepMetaMap[elementName].fields ?? [];
    const result = fields.reduce((acc, field) => {
        acc[field] = store?.[field];
        return acc;
    }, {} as Record<string, any>);

    result.id = store?.id;

    return result;
};

export type EventType =
    | 'clicked_on_bys_niche_card'
    | 'clicked_on_next'
    | 'clicked_on_sign_up_to_shopify'
    | 'clicked_on_access_shopify'
    | 'clicked_on_install_app'
    | 'clicked_on_subscribe_to_shopify'
    | 'clicked_on_create_my_store' // TODO: add this
    | 'clicked_on_preview_store'
    | 'clicked_on_autods_sign_up'
    | 'clicked_on_bys_gig_card'
    | 'clicked_on_gigs_type_header'
    | 'clicked_on_publish_my_store'
    | 'clicked_on_shopify_services_or_bys_toggle'
    | 'clicked_on_bys_sticky_component';

export interface BiEventData {
    userId: number | undefined;
    ctxId: string;
    eventType?: EventType;
}

export interface ReportBiEventParams {
    store?: Partial<Store> | undefined;
    biEventData: BiEventData;
    activityType: BiankaEventType;
    eventType: EventType;
    elementName: string;
    elementType: ElementType;
    pageName: string;
    tabbedCarouselTabClickData?: TabbedCarouselTabClickData;
    storeBuilderToggleChoice?: 'shopify_services' | 'freelancer_store_builder';
}
